import Pagination from '../../../components/shared/pagination';
import React, { useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { RxCaretSort } from 'react-icons/rx';
import { RiArrowUpSLine } from "react-icons/ri";
import { formatUTCTimestampToLocal } from '../../../utils/set-date-range-utils';
import Loader from '../../../components/shared/loader';
import { camelCaseToWords } from '../../../utils/structure-words-utils';


const CategoryExpenseTable = ({ data, setData, globalFilter, onGlobalFilterChange, loadingData, attributes }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sorting, setSorting] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const closeMenu = () => {
    setOpenMenuId(null);
  };

  const handleRowClick = (row, e) => {
    if (!e.target.closest('input[type="checkbox"]')) {
      setSelectedRow(row.original);
    }
  };


  useEffect(() => {
    const handleClickOutside = () => closeMenu();
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);


  // Define columns with sorting configuration
  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <div className="flex justify-center w-full">
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                table.getToggleAllRowsSelectedHandler()(e);
              }}
              className="w-4 h-4"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={(e) => {
                e.stopPropagation(); // Stop event bubbling
                row.getToggleSelectedHandler()(e); // Toggle checkbox
              }}
              className="w-4 h-4"
            />
          </div>
        ),
        enableSorting: false,
        size: 50,
      },
      {
        accessorKey: 'category',
        header: 'category',
        enableSorting: true,
        cell: info => camelCaseToWords(info.getValue()) || '----',
      },
      {
        accessorKey: 'jobs',
        header: `Job Cost (${attributes?.currency})`,
        enableSorting: true,
      },
      {
        accessorKey: 'vehicles',
        header: `Vehicle Cost (${attributes?.currency})`,
        enableSorting: true,
      },
      {
        accessorKey: 'others',
        header: `Others Cost (${attributes?.currency})`,
        enableSorting: true,
      },
      {
        accessorKey: 'total',
        header: `Total Cost (${attributes?.currency})`,
        enableSorting: true,
      },
    ],
    [openMenuId]
  );


  // Initialize the table
  const table = useReactTable({
    data, // Use the full dataset - no pre-filtering needed
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    enableSortingRemoval: false,
    // Add global filter function
    globalFilterFn: (row, columnId, filterValue) => {
      const searchLower = filterValue.toLowerCase();
      const rowValue = String(row.getValue(columnId)).toLowerCase();

      if (columnId === 'expenseTime') {
        return formatUTCTimestampToLocal(rowValue, 'yyyy-MM-dd hh:mm a').toLowerCase().includes(searchLower);
      } else if (columnId.includes('.')) {
        const [parent, child] = columnId.split('.');
        const value = row.original[parent]?.[child] || '';
        return String(value).toLowerCase().includes(searchLower);
      }

      return rowValue.includes(searchLower);
    },
  });


  return (
    <div className='w-full'>
      <div className="overflow-x-auto max-w-full pb-8">
        {loadingData ? (
          <Loader />
        ) : (
          <table className="min-w-full text-sm border border-gray-200">
            <colgroup>
              <col style={{ width: '50px' }} />
              <col />
              <col />
              <col />
              <col />
            </colgroup>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id} className="bg-gray-100 capitalize text-left">
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="px-2 py-1.5 border-b capitalize"
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        width: header.column.getSize(),
                      }}
                    >
                      <div className="flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() && (
                          <span className="text-black">
                            {{
                              asc: <RiArrowUpSLine className="text-base mt-0.5" />,
                              desc: <RiArrowUpSLine className="text-base rotate-180" />,
                            }[header.column.getIsSorted()] ?? (
                                <RxCaretSort className="text-lg" />
                              )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows
                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map(row => (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => handleRowClick(row, e)}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        className="px-2 py-1 border-b whitespace-nowrap"
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={table.getFilteredRowModel().rows.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(count) => {
          setItemsPerPage(count);
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default CategoryExpenseTable; 